(function () {
  'use strict';

  if (window.UnifiedValidator) {
    return;
  }

  const VERSION = '1.1.0';

  const LEGACY_DEBUG_EVENT_NAME =
    'unified:debug';

  /*
   * ------------------------------------------------------------
   * BASIC HELPERS
   * ------------------------------------------------------------
   */

  function getConfig() {
    return (
      window.UnifiedTrackingConfig ||
      {}
    );
  }

  function getSchema() {
    return (
      window.UnifiedSchema ||
      null
    );
  }

  function isDebugEnabled() {
    return (
      getConfig().debug === true
    );
  }

  function isPlainObject(value) {
    return Boolean(
      value &&
      typeof value === 'object' &&
      !Array.isArray(value)
    );
  }

  function hasOwn(
    object,
    key
  ) {
    return Object.prototype
      .hasOwnProperty.call(
        object,
        key
      );
  }

  /*
   * ------------------------------------------------------------
   * PERFORMANCE
   * ------------------------------------------------------------
   */

  function performanceStart(
    label,
    meta
  ) {
    if (
      window.UnifiedPerformance &&
      typeof window.UnifiedPerformance.start ===
        'function'
    ) {
      return window.UnifiedPerformance.start(
        label,
        meta || {}
      );
    }

    return null;
  }

  function performanceEnd(
    tokenId,
    meta
  ) {
    if (
      tokenId === null ||
      typeof tokenId === 'undefined'
    ) {
      return null;
    }

    if (
      window.UnifiedPerformance &&
      typeof window.UnifiedPerformance.end ===
        'function'
    ) {
      return window.UnifiedPerformance.end(
        tokenId,
        meta || {}
      );
    }

    return null;
  }

  /*
   * ------------------------------------------------------------
   * ERROR REPORTING
   * ------------------------------------------------------------
   */

  function reportError(
    severity,
    code,
    message,
    details
  ) {
    if (
      window.UnifiedErrors &&
      typeof window.UnifiedErrors.report ===
        'function'
    ) {
      try {
        return window.UnifiedErrors.report(
          severity,
          code,
          message,
          details || {}
        );
      } catch (error) {
        // Console fallback below.
      }
    }

    const method =
      severity === 'warning'
        ? 'warn'
        : 'error';

    console[method](
      '[Unified Validator] ' +
        message,
      details || {}
    );

    return null;
  }

  /*
   * ------------------------------------------------------------
   * EVENT EMISSION
   * ------------------------------------------------------------
   */

  function emitLegacyEvent(
    type,
    data
  ) {
    try {
      const detail = {
        type: type,
        timestamp:
          new Date().toISOString(),
        data:
          data || {}
      };

      window.dispatchEvent(
        new CustomEvent(
          LEGACY_DEBUG_EVENT_NAME,
          {
            detail: detail
          }
        )
      );

      return detail;
    } catch (error) {
      return null;
    }
  }

  function emitEvent(
    type,
    data
  ) {
    if (
      window.UnifiedBus &&
      typeof window.UnifiedBus.emit ===
        'function'
    ) {
      try {
        return window.UnifiedBus.emit(
          type,
          data || {}
        );
      } catch (error) {
        reportError(
          'warning',
          'validator_bus_emit_failed',
          'Unified Validator could not emit through Unified Bus.',
          {
            event_type:
              type,
            error:
              error.message
          }
        );
      }
    }

    return emitLegacyEvent(
      type,
      data
    );
  }

  /*
   * ------------------------------------------------------------
   * TYPE VALIDATION
   * ------------------------------------------------------------
   */

  function validateType(
    value,
    expectedType
  ) {
    switch (expectedType) {
      case 'string':
        return (
          typeof value ===
          'string'
        );

      case 'number':
        return (
          typeof value ===
            'number' &&
          Number.isFinite(value)
        );

      case 'boolean':
        return (
          typeof value ===
          'boolean'
        );

      case 'array':
        return (
          Array.isArray(value)
        );

      case 'object':
        return (
          isPlainObject(value)
        );

      default:
        return false;
    }
  }

  /*
   * ------------------------------------------------------------
   * FIELD VALIDATION
   * ------------------------------------------------------------
   */

  function validateField(
    eventName,
    fieldName,
    value,
    definition
  ) {
    const errors = [];
    const warnings = [];

    if (!definition) {
      errors.push({
        code:
          'field_definition_missing',

        field:
          fieldName,

        message:
          'Field is not defined in Unified Schema.'
      });

      return {
        valid: false,
        errors: errors,
        warnings: warnings
      };
    }

    if (
      typeof value ===
      'undefined'
    ) {
      if (
        definition.required ===
        true
      ) {
        errors.push({
          code:
            'required_field_missing',

          field:
            fieldName,

          message:
            'Required field is missing.'
        });
      }

      return {
        valid:
          errors.length === 0,

        errors:
          errors,

        warnings:
          warnings
      };
    }

    if (
      value === null
    ) {
      errors.push({
        code:
          'field_null',

        field:
          fieldName,

        message:
          'Field must not be null.'
      });

      return {
        valid: false,
        errors: errors,
        warnings: warnings
      };
    }

    if (
      !validateType(
        value,
        definition.type
      )
    ) {
      errors.push({
        code:
          'field_type_invalid',

        field:
          fieldName,

        expected_type:
          definition.type,

        received_type:
          Array.isArray(value)
            ? 'array'
            : typeof value,

        message:
          'Field has an invalid type.'
      });

      return {
        valid: false,
        errors: errors,
        warnings: warnings
      };
    }

    /*
     * Required strings cannot be empty.
     */

    if (
      definition.type ===
        'string' &&
      definition.required ===
        true &&
      value.trim() === ''
    ) {
      errors.push({
        code:
          'required_string_empty',

        field:
          fieldName,

        message:
          'Required string must not be empty.'
      });
    }

    /*
     * Number boundaries.
     */

    if (
      definition.type ===
      'number'
    ) {
      if (
        typeof definition.minimum ===
          'number' &&
        value <
          definition.minimum
      ) {
        errors.push({
          code:
            'number_below_minimum',

          field:
            fieldName,

          minimum:
            definition.minimum,

          message:
            'Number is below the allowed minimum.'
        });
      }

      if (
        typeof definition.maximum ===
          'number' &&
        value >
          definition.maximum
      ) {
        errors.push({
          code:
            'number_above_maximum',

          field:
            fieldName,

          maximum:
            definition.maximum,

          message:
            'Number exceeds the allowed maximum.'
        });
      }
    }

    /*
     * Privacy safeguard.
     *
     * A field explicitly classified as excluded_sensitive
     * must never enter the analytics pipeline.
     */

    if (
      definition.analytics ===
      'excluded_sensitive'
    ) {
      errors.push({
        code:
          'sensitive_field_blocked',

        field:
          fieldName,

        message:
          'Sensitive field is not allowed in the analytics payload.'
      });
    }

    return {
      valid:
        errors.length === 0,

      errors:
        errors,

      warnings:
        warnings
    };
  }

  /*
   * ------------------------------------------------------------
   * CONTRACT VALIDATION
   * ------------------------------------------------------------
   */

  function validateRequiredFields(
    eventName,
    payload,
    schema
  ) {
    const errors = [];

    const requiredFields =
      schema.getRequiredFields(
        eventName
      );

    requiredFields.forEach(
      function (fieldName) {
        if (
          !hasOwn(
            payload,
            fieldName
          ) ||
          typeof payload[
            fieldName
          ] === 'undefined'
        ) {
          errors.push({
            code:
              'required_field_missing',

            field:
              fieldName,

            message:
              'Required field is missing.'
          });
        }
      }
    );

    return errors;
  }

  function validateUnknownFields(
    eventName,
    payload,
    schema
  ) {
    const errors = [];

    const allowedFields =
      new Set(
        schema.getAllowedFields(
          eventName
        )
      );

    Object.keys(payload)
      .forEach(
        function (
          fieldName
        ) {
          if (
            allowedFields.has(
              fieldName
            )
          ) {
            return;
          }

          /*
           * IMPORTANT:
           * We deliberately do not log the value
           * of an unknown field.
           */

          errors.push({
            code:
              'unknown_field_blocked',

            field:
              fieldName,

            message:
              'Field is not allowed by the analytics contract.'
          });
        }
      );

    return errors;
  }

  function validateEventCategory(
    eventName,
    payload,
    eventSchema
  ) {
    const errors = [];

    if (
      !eventSchema ||
      !eventSchema.category
    ) {
      return errors;
    }

    if (
      payload.event_category !==
      eventSchema.category
    ) {
      errors.push({
        code:
          'event_category_mismatch',

        field:
          'event_category',

        expected:
          eventSchema.category,

        received:
          payload.event_category,

        message:
          'Event category does not match the Unified Schema contract.'
      });
    }

    return errors;
  }

  /*
   * ------------------------------------------------------------
   * MAIN VALIDATOR
   * ------------------------------------------------------------
   */

  function validate(
    eventName,
    payload
  ) {
    const performanceToken =
      performanceStart(
        'validator.total',
        {
          event:
            eventName
        }
      );

    const errors = [];
    const warnings = [];

    const schema =
      getSchema();

    /*
     * Schema must exist.
     */

    if (
      !schema ||
      typeof schema.getEventSchema !==
        'function' ||
      typeof schema.getRequiredFields !==
        'function' ||
      typeof schema.getAllowedFields !==
        'function' ||
      typeof schema.getFieldDefinition !==
        'function'
    ) {
      const result = {
        valid: false,
        event: eventName,
        errors: [
          {
            code:
              'schema_unavailable',

            message:
              'Unified Schema is unavailable or incomplete.'
          }
        ],
        warnings: []
      };

      reportError(
        'error',
        'schema_unavailable',
        'Unified Validator cannot validate without Unified Schema.',
        {
          event:
            eventName
        }
      );

      performanceEnd(
        performanceToken,
        {
          event:
            eventName,

          status:
            'error',

          errors:
            1
        }
      );

      return result;
    }

    /*
     * Event name must be valid.
     */

    if (
      typeof eventName !==
        'string' ||
      eventName.trim() === ''
    ) {
      const result = {
        valid: false,
        event: eventName,
        errors: [
          {
            code:
              'event_name_invalid',

            message:
              'Event name must be a non-empty string.'
          }
        ],
        warnings: []
      };

      performanceEnd(
        performanceToken,
        {
          status:
            'error',

          reason:
            'event_name_invalid'
        }
      );

      return result;
    }

    const eventSchema =
      schema.getEventSchema(
        eventName
      );

    /*
     * Unknown events are blocked.
     */

    if (!eventSchema) {
      const result = {
        valid: false,
        event: eventName,
        errors: [
          {
            code:
              'event_unknown',

            message:
              'Event is not defined in Unified Schema.'
          }
        ],
        warnings: []
      };

      reportError(
        'warning',
        'event_unknown',
        'Unified Validator blocked an unknown event.',
        {
          event:
            eventName
        }
      );

      emitEvent(
        'event_validation_failed',
        {
          event:
            eventName,

          valid:
            false,

          errors:
            result.errors,

          warnings:
            []
        }
      );

      performanceEnd(
        performanceToken,
        {
          event:
            eventName,

          status:
            'blocked',

          errors:
            1
        }
      );

      return result;
    }

    /*
     * Payload must be a plain object.
     */

    if (
      !isPlainObject(
        payload
      )
    ) {
      const result = {
        valid: false,
        event: eventName,
        errors: [
          {
            code:
              'payload_invalid',

            message:
              'Payload must be a plain object.'
          }
        ],
        warnings: []
      };

      reportError(
        'warning',
        'payload_invalid',
        'Unified Validator received an invalid payload.',
        {
          event:
            eventName
        }
      );

      emitEvent(
        'event_validation_failed',
        {
          event:
            eventName,

          valid:
            false,

          errors:
            result.errors,

          warnings:
            []
        }
      );

      performanceEnd(
        performanceToken,
        {
          event:
            eventName,

          status:
            'blocked',

          errors:
            1
        }
      );

      return result;
    }

    /*
     * Unknown fields first.
     *
     * This acts as an analytics allowlist.
     */

    errors.push.apply(
      errors,
      validateUnknownFields(
        eventName,
        payload,
        schema
      )
    );

    /*
     * Required fields.
     */

    errors.push.apply(
      errors,
      validateRequiredFields(
        eventName,
        payload,
        schema
      )
    );

    /*
     * Validate all known fields.
     */

    Object.keys(payload)
      .forEach(
        function (
          fieldName
        ) {
          const definition =
            schema.getFieldDefinition(
              eventName,
              fieldName
            );

          /*
           * Unknown fields were already
           * registered above.
           */

          if (!definition) {
            return;
          }

          const fieldResult =
            validateField(
              eventName,
              fieldName,
              payload[
                fieldName
              ],
              definition
            );

          if (
            fieldResult.errors.length
          ) {
            errors.push.apply(
              errors,
              fieldResult.errors
            );
          }

          if (
            fieldResult.warnings.length
          ) {
            warnings.push.apply(
              warnings,
              fieldResult.warnings
            );
          }
        }
      );

    /*
     * Event category must match schema.
     */

    errors.push.apply(
      errors,
      validateEventCategory(
        eventName,
        payload,
        eventSchema
      )
    );

    /*
     * Remove duplicate errors generated by
     * required field + field validation.
     */

    const seenErrors =
      new Set();

    const uniqueErrors =
      errors.filter(
        function (error) {
          const key = [
            error.code || '',
            error.field || '',
            error.message || ''
          ].join('|');

          if (
            seenErrors.has(key)
          ) {
            return false;
          }

          seenErrors.add(key);

          return true;
        }
      );

    const valid =
      uniqueErrors.length === 0;

    const result = {
      valid:
        valid,

      event:
        eventName,

      errors:
        uniqueErrors,

      warnings:
        warnings
    };

    /*
     * Lifecycle event.
     */

    emitEvent(
      valid
        ? 'event_validated'
        : 'event_validation_failed',
      {
        event:
          eventName,

        valid:
          valid,

        errors:
          uniqueErrors,

        warnings:
          warnings
      }
    );

    /*
     * Central error registry.
     */

    if (!valid) {
      reportError(
        'warning',
        'event_validation_failed',
        'Unified Validator blocked an event that failed schema validation.',
        {
          event:
            eventName,

          error_count:
            uniqueErrors.length,

          fields:
            uniqueErrors.map(
              function (
                error
              ) {
                return (
                  error.field ||
                  null
                );
              }
            )
        }
      );
    }

    performanceEnd(
      performanceToken,
      {
        event:
          eventName,

        status:
          valid
            ? 'success'
            : 'blocked',

        errors:
          uniqueErrors.length,

        warnings:
          warnings.length
      }
    );

    if (
      isDebugEnabled()
    ) {
      console.info(
        '[Unified Validator] Event validation completed',
        {
          event:
            eventName,

          valid:
            valid,

          errors:
            uniqueErrors.length,

          warnings:
            warnings.length
        }
      );
    }

    return result;
  }

  /*
   * ------------------------------------------------------------
   * INITIALIZATION
   * ------------------------------------------------------------
   */

  const schema =
    getSchema();

  if (
    !schema ||
    typeof schema.getEventSchema !==
      'function'
  ) {
    console.error(
      '[Unified Validator] Initialization failed. unified-schema.js must load first.'
    );

    return;
  }

  window.UnifiedValidator = {
    version:
      VERSION,

    validate:
      validate,

    validateField:
      validateField
  };

  if (
    isDebugEnabled()
  ) {
    console.info(
      '[Unified Validator] Validator initialized',
      {
        version:
          VERSION,

        schemaVersion:
          schema.version ||
          'unknown'
      }
    );
  }
})();